'use client';

import type { MoneyValue } from '@akademi/site-builder-runtime';
import { formatMoney } from '@/src/lib/money';
import { onBasis, type PricedMoney, type VatBasis } from '@/src/lib/vatDisplay';
import { useVatDisplay } from './VatDisplay';

export interface PriceProps {
  value?: MoneyValue;
  listPrice?: MoneyValue;
  locale: string;
  labels: { taxIncluded: string; taxExcluded: string; was: string; onRequest: string };
  className?: string;
  /**
   * Show the other tax basis beneath the headline, rather than only the chosen one.
   *
   * <p>A trade catalogue quotes both at once: the consumer reads the inc VAT figure the
   * Price Marking Order requires as the headline, and the installer reads the ex VAT
   * figure they will actually be invoiced and will reclaim. Making that a toggle — one
   * basis at a time — serves whichever visitor set it last and makes the other one do
   * arithmetic on a page where they are comparing fourteen line items.
   *
   * <p>It costs no extra request: `alternate` already travels on every unit price.
   * Where the backend could not resolve the tax there is no alternate, and the second
   * line is simply absent rather than computed here — see `onBasis`.
   */
  showAlternate?: boolean;
  /** Smaller headline, for a dense listing card. */
  compact?: boolean;
}

/**
 * A price, and the tax basis it is quoted on.
 *
 * <p>Renders nothing at all when there is no price. A `TRADE_ONLY` product
 * carries none — the backend deliberately sends null rather than a zero — and a
 * placeholder figure would be a lie about what the thing costs.
 *
 * <p>`taxInclusive` is rendered as a label, never re-derived: whether a channel
 * quotes inclusive or exclusive is a property of the channel, and a storefront
 * that recomputes it will be wrong in one of the two countries this platform
 * runs in. The same goes for the figure: which basis is shown — inc VAT for the
 * public, the trade customer's choice for them (see `useVatDisplay`) — only picks
 * between the published figure and the backend's `alternate`; nothing here
 * adds or removes tax.
 */
export function Price(props: PriceProps) {
  const { basis } = useVatDisplay();
  return <PriceView {...props} basis={basis} />;
}

/** {@link Price} on a given basis. */
export function PriceView({
  value,
  listPrice,
  locale,
  labels,
  className,
  basis,
  showAlternate = false,
  compact = false,
}: PriceProps & { basis: VatBasis }) {
  const shown = value ? onBasis(value as PricedMoney, basis) : undefined;
  const formatted = formatMoney(shown, locale);
  if (!formatted || !shown) {
    return <p className={className}>{labels.onRequest}</p>;
  }
  // The basis the headline is NOT on. `onBasis` returns the published figure unchanged
  // when it cannot honour the request, so an unresolvable tax gives back the same
  // amount — compared here rather than trusted, so the card never prints one price
  // twice under two different labels.
  const other = showAlternate
    ? onBasis(value as PricedMoney, basis === 'inc' ? 'ex' : 'inc')
    : undefined;
  const otherFormatted =
    other && other.taxInclusive !== shown.taxInclusive ? formatMoney(other, locale) : null;
  // An absent listPrice means "no discount". It is NOT the same as a listPrice
  // equal to the price, so nothing is struck through unless the backend sent a
  // genuinely different was-price — on the same basis as the price beside it.
  const shownList = listPrice ? onBasis(listPrice as PricedMoney, basis) : undefined;
  const was =
    shownList && shownList.taxInclusive === shown.taxInclusive && shownList.amount !== shown.amount
      ? formatMoney(shownList, locale)
      : null;

  return (
    <div className={className}>
      <p>
        <span className={compact ? 'text-xl font-bold' : 'text-2xl font-semibold'}>
          {formatted}
        </span>
        {was ? (
          <>
            {' '}
            <span className="text-sm text-[var(--color-text-muted)] line-through">
              <span className="sr-only">{labels.was} </span>
              {was}
            </span>
          </>
        ) : null}{' '}
        <span className="text-sm text-[var(--color-text-secondary)]">
          {shown.taxInclusive ? labels.taxIncluded : labels.taxExcluded}
        </span>
      </p>
      {otherFormatted ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          {otherFormatted}{' '}
          <span>{other!.taxInclusive ? labels.taxIncluded : labels.taxExcluded}</span>
        </p>
      ) : null}
    </div>
  );
}
